import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ChevronLeft, Plus, Trash2, DollarSign, MapPin, Radio, Ticket } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card } from '@/components/ui/card';
import { AdminLayout } from '@/components/layout/AdminLayout';
import { toast } from 'sonner';

const steps = [
  { id: 'basics', label: 'Basics' },
  { id: 'location', label: 'Location' },
  { id: 'media', label: 'Media' },
  { id: 'tickets', label: 'Tickets' }
];

const StepIndicator = ({ currentStep }) => (
  <div className="flex items-center gap-2 mb-8">
    {steps.map((step, index) => (
      <React.Fragment key={step.id}>
        <div className={`flex items-center gap-2 ${
          step.id === currentStep ? 'text-primary' : 
          steps.findIndex(s => s.id === currentStep) > index ? 'text-primary' : 'text-muted-foreground'
        }`}>
          <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold ${
            step.id === currentStep 
              ? 'bg-primary text-primary-foreground' 
              : steps.findIndex(s => s.id === currentStep) > index
              ? 'bg-primary/30 text-primary'
              : 'bg-secondary'
          }`}>
            {index + 1}
          </div>
          <span className="hidden md:inline text-sm">{step.label}</span>
        </div>
        {index < steps.length - 1 && (
          <div className="flex-1 h-px bg-border max-w-[60px]" />
        )}
      </React.Fragment>
    ))}
  </div>
);

// Ticket Type Badge
const TicketTypeBadge = ({ type }) => {
  const isIRL = type === 'irl';
  return (
    <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium ${
      isIRL ? 'bg-purple-500/20 text-purple-400' : 'bg-cyan-500/20 text-cyan-400'
    }`}>
      {isIRL ? <MapPin className="w-3 h-3" /> : <Radio className="w-3 h-3" />}
      {isIRL ? 'In-Person (IRL)' : 'Livestream'}
    </span>
  );
};

export function AdminCreateEventTickets() {
  const navigate = useNavigate();
  const [tickets, setTickets] = useState([
    { id: 1, name: 'General Admission', price: '50', quantity: '200', ticketType: 'irl' },
    { id: 2, name: 'VIP', price: '150', quantity: '50', ticketType: 'irl' },
    { id: 3, name: 'Livestream Access', price: '25', quantity: '1000', ticketType: 'livestream' }
  ]);

  const addTicketType = (type) => {
    const defaultName = type === 'irl' ? 'New IRL Ticket' : 'New Livestream Ticket';
    setTickets([...tickets, { id: Date.now(), name: defaultName, price: '', quantity: '', ticketType: type }]);
  };

  const removeTicketType = (id) => {
    setTickets(tickets.filter(t => t.id !== id));
  };

  const updateTicket = (id, field, value) => {
    setTickets(tickets.map(t => t.id === id ? {...t, [field]: value} : t));
  };

  const handleBack = () => {
    navigate('/admin/events/new/media');
  };

  const handleCreate = () => {
    toast.success('Event created successfully!');
    toast.info('Note: Event data will not persist (frontend-only behavior)');
    navigate('/admin');
  };

  const irlTickets = tickets.filter(t => t.ticketType === 'irl');
  const livestreamTickets = tickets.filter(t => t.ticketType === 'livestream');

  return (
    <AdminLayout>
      <div className="p-8 max-w-2xl mx-auto">
        <Link to="/admin" className="inline-flex items-center text-muted-foreground hover:text-white mb-6">
          <ChevronLeft className="w-5 h-5 mr-1" />
          Back to Events
        </Link>

        <h1 className="text-3xl font-bold text-white mb-2">Event Tickets</h1>
        <p className="text-muted-foreground mb-6">Set up ticket types and pricing</p>

        <StepIndicator currentStep="tickets" />

        <Card className="bg-card border-border p-6">
          <div className="space-y-6">
            {/* IRL Tickets Section */}
            <div>
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <MapPin className="w-5 h-5 text-purple-400" />
                  <h3 className="text-lg font-semibold text-white">In-Person (IRL) Tickets</h3>
                </div>
                <Button 
                  variant="ghost" 
                  size="sm"
                  className="text-primary"
                  onClick={() => addTicketType('irl')}
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Add IRL Ticket
                </Button>
              </div>
              
              <div className="space-y-3">
                {irlTickets.map((ticket, index) => (
                  <Card key={ticket.id} className="bg-secondary/30 border-purple-500/30 p-4">
                    <div className="flex items-start justify-between mb-3">
                      <TicketTypeBadge type="irl" />
                      {irlTickets.length > 1 && (
                        <button 
                          onClick={() => removeTicketType(ticket.id)}
                          className="text-muted-foreground hover:text-destructive"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                    
                    <div className="space-y-3">
                      <div>
                        <label className="text-xs text-muted-foreground mb-1 block">Ticket Name</label>
                        <Input 
                          placeholder="e.g., General Admission, VIP"
                          value={ticket.name}
                          onChange={(e) => updateTicket(ticket.id, 'name', e.target.value)}
                          className="bg-card border-border"
                        />
                      </div>
                      
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="text-xs text-muted-foreground mb-1 block">Price</label>
                          <div className="relative">
                            <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                            <Input 
                              type="number"
                              placeholder="0.00"
                              value={ticket.price}
                              onChange={(e) => updateTicket(ticket.id, 'price', e.target.value)}
                              className="bg-card border-border pl-8"
                            />
                          </div>
                        </div>
                        <div>
                          <label className="text-xs text-muted-foreground mb-1 block">Quantity</label>
                          <Input 
                            type="number"
                            placeholder="100"
                            value={ticket.quantity}
                            onChange={(e) => updateTicket(ticket.id, 'quantity', e.target.value)}
                            className="bg-card border-border"
                          />
                        </div>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            </div>

            {/* Livestream Tickets Section */}
            <div>
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <Radio className="w-5 h-5 text-cyan-400" />
                  <h3 className="text-lg font-semibold text-white">Livestream Tickets</h3>
                </div>
                <Button 
                  variant="ghost" 
                  size="sm"
                  className="text-primary"
                  onClick={() => addTicketType('livestream')}
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Add Livestream Ticket
                </Button>
              </div>
              
              <div className="space-y-3">
                {livestreamTickets.length === 0 ? (
                  <Card className="bg-secondary/30 border-border border-dashed p-6 text-center">
                    <Radio className="w-8 h-8 text-cyan-400/50 mx-auto mb-2" />
                    <p className="text-sm text-muted-foreground">No livestream tickets configured</p>
                    <Button 
                      variant="ghost" 
                      size="sm"
                      className="text-primary mt-2"
                      onClick={() => addTicketType('livestream')}
                    >
                      <Plus className="w-4 h-4 mr-1" />
                      Add Livestream Ticket
                    </Button>
                  </Card>
                ) : (
                  livestreamTickets.map((ticket) => (
                    <Card key={ticket.id} className="bg-secondary/30 border-cyan-500/30 p-4">
                      <div className="flex items-start justify-between mb-3">
                        <TicketTypeBadge type="livestream" />
                        <button 
                          onClick={() => removeTicketType(ticket.id)}
                          className="text-muted-foreground hover:text-destructive"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                      
                      <div className="space-y-3">
                        <div>
                          <label className="text-xs text-muted-foreground mb-1 block">Ticket Name</label>
                          <Input 
                            placeholder="e.g., Livestream Access"
                            value={ticket.name}
                            onChange={(e) => updateTicket(ticket.id, 'name', e.target.value)}
                            className="bg-card border-border"
                          />
                        </div>
                        
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <label className="text-xs text-muted-foreground mb-1 block">Price</label>
                            <div className="relative">
                              <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                              <Input 
                                type="number"
                                placeholder="0.00"
                                value={ticket.price}
                                onChange={(e) => updateTicket(ticket.id, 'price', e.target.value)}
                                className="bg-card border-border pl-8"
                              />
                            </div>
                          </div>
                          <div>
                            <label className="text-xs text-muted-foreground mb-1 block">Quantity</label>
                            <Input 
                              type="number"
                              placeholder="Unlimited"
                              value={ticket.quantity}
                              onChange={(e) => updateTicket(ticket.id, 'quantity', e.target.value)}
                              className="bg-card border-border"
                            />
                          </div>
                        </div>
                      </div>
                    </Card>
                  ))
                )}
              </div>
            </div>

            {/* Summary */}
            <Card className="bg-primary/10 border-primary/30 p-4">
              <h4 className="text-sm font-medium text-white mb-3 flex items-center gap-2">
                <Ticket className="w-4 h-4" />
                Summary
              </h4>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between text-muted-foreground">
                  <span className="flex items-center gap-2">
                    <MapPin className="w-3 h-3 text-purple-400" />
                    IRL Tickets
                  </span>
                  <span className="text-white">
                    {irlTickets.length} type{irlTickets.length !== 1 ? 's' : ''} • {irlTickets.reduce((sum, t) => sum + (parseInt(t.quantity) || 0), 0)} total
                  </span>
                </div>
                <div className="flex justify-between text-muted-foreground">
                  <span className="flex items-center gap-2">
                    <Radio className="w-3 h-3 text-cyan-400" />
                    Livestream Tickets
                  </span>
                  <span className="text-white">
                    {livestreamTickets.length} type{livestreamTickets.length !== 1 ? 's' : ''} • {livestreamTickets.reduce((sum, t) => sum + (parseInt(t.quantity) || 0), 0)} total
                  </span>
                </div>
                <div className="flex justify-between pt-2 border-t border-border">
                  <span className="text-white font-medium">Total Capacity</span>
                  <span className="text-primary font-bold">
                    {tickets.reduce((sum, t) => sum + (parseInt(t.quantity) || 0), 0)} tickets
                  </span>
                </div>
              </div>
            </Card>

            <div className="flex gap-3">
              <Button 
                variant="outline" 
                className="flex-1 border-border"
                onClick={handleBack}
              >
                Back
              </Button>
              <Button 
                className="flex-1 bg-primary text-primary-foreground hover:bg-primary/90"
                onClick={handleCreate}
                data-testid="create-event-btn"
              >
                Create Event
              </Button>
            </div>
          </div>
        </Card>

        <p className="text-xs text-muted-foreground mt-4 text-center">
          ⚠️ Note: Newly created events will not persist (frontend-only behavior).
        </p>
      </div>
    </AdminLayout>
  );
}

export default AdminCreateEventTickets;
